import { useCallback, useEffect, useRef } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { useStatefulObservableEffect } from '@atlassian/rx-hooks';

import { type AIAgentStreamingRequest } from '../api/AIAgentStreamingModels';
import { aiAgentStreamingEffect } from '../effects/aiAgentStreamingEffect';
import { type UserAIAgentStreamingConfig } from '../models/AIAgentStreamingConfig';
import {
	type AIAgentStreamingState,
	getDefaultAIAgentStreamingState,
} from '../models/AIAgentStreamingState';

export function useAIAgentStreaming<
	TAIAgentStreamingRequest extends AIAgentStreamingRequest,
	TAIAgentContentResponse,
>(
	request: TAIAgentStreamingRequest,
	config: UserAIAgentStreamingConfig,
	skip: boolean = false,
	onStateChange?: (state: AIAgentStreamingState<TAIAgentContentResponse>) => void,
): [AIAgentStreamingState<TAIAgentContentResponse>, () => void] {
	const refetchId = useRef<string>(uuidv4());

	const [state, setState] = useStatefulObservableEffect<
		AIAgentStreamingState<TAIAgentContentResponse>,
		[TAIAgentStreamingRequest, UserAIAgentStreamingConfig, string, boolean]
	>(aiAgentStreamingEffect, getDefaultAIAgentStreamingState(), [
		request,
		config,
		refetchId.current,
		skip,
	]);

	useEffect(() => {
		onStateChange?.(state);
	}, [state, onStateChange]);

	const refetch = useCallback(() => {
		refetchId.current = uuidv4();
		setState({
			inputs: [request, config, refetchId.current, skip],
			state: getDefaultAIAgentStreamingState(),
		});
	}, [request, config, skip, setState]);

	return [state, refetch];
}
